import React, { useState, useEffect, forwardRef } from 'react';
import { parsePhoneNumber, AsYouType, CountryCode } from 'libphonenumber-js';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';

export interface PhoneInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value?: string;
  onChange?: (value: string) => void;
  defaultCountry?: CountryCode;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const countries = [
  { code: 'AU' as CountryCode, name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { code: 'US' as CountryCode, name: 'United States', dialCode: '+1', flag: '🇺🇸' },
  { code: 'GB' as CountryCode, name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
  { code: 'CA' as CountryCode, name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'NZ' as CountryCode, name: 'New Zealand', dialCode: '+64', flag: '🇳🇿' },
  { code: 'DE' as CountryCode, name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
  { code: 'FR' as CountryCode, name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'IT' as CountryCode, name: 'Italy', dialCode: '+39', flag: '🇮🇹' },
  { code: 'ES' as CountryCode, name: 'Spain', dialCode: '+34', flag: '🇪🇸' },
  { code: 'NL' as CountryCode, name: 'Netherlands', dialCode: '+31', flag: '🇳🇱' },
  { code: 'BE' as CountryCode, name: 'Belgium', dialCode: '+32', flag: '🇧🇪' },
  { code: 'CH' as CountryCode, name: 'Switzerland', dialCode: '+41', flag: '🇨🇭' },
  { code: 'AT' as CountryCode, name: 'Austria', dialCode: '+43', flag: '🇦🇹' },
  { code: 'SE' as CountryCode, name: 'Sweden', dialCode: '+46', flag: '🇸🇪' },
  { code: 'NO' as CountryCode, name: 'Norway', dialCode: '+47', flag: '🇳🇴' },
  { code: 'DK' as CountryCode, name: 'Denmark', dialCode: '+45', flag: '🇩🇰' },
  { code: 'FI' as CountryCode, name: 'Finland', dialCode: '+358', flag: '🇫🇮' },
  { code: 'IE' as CountryCode, name: 'Ireland', dialCode: '+353', flag: '🇮🇪' },
  { code: 'PT' as CountryCode, name: 'Portugal', dialCode: '+351', flag: '🇵🇹' },
  { code: 'GR' as CountryCode, name: 'Greece', dialCode: '+30', flag: '🇬🇷' },
  { code: 'JP' as CountryCode, name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
  { code: 'KR' as CountryCode, name: 'South Korea', dialCode: '+82', flag: '🇰🇷' },
  { code: 'CN' as CountryCode, name: 'China', dialCode: '+86', flag: '🇨🇳' },
  { code: 'IN' as CountryCode, name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'SG' as CountryCode, name: 'Singapore', dialCode: '+65', flag: '🇸🇬' },
  { code: 'MY' as CountryCode, name: 'Malaysia', dialCode: '+60', flag: '🇲🇾' },
  { code: 'TH' as CountryCode, name: 'Thailand', dialCode: '+66', flag: '🇹🇭' },
  { code: 'PH' as CountryCode, name: 'Philippines', dialCode: '+63', flag: '🇵🇭' },
  { code: 'VN' as CountryCode, name: 'Vietnam', dialCode: '+84', flag: '🇻🇳' },
  { code: 'ID' as CountryCode, name: 'Indonesia', dialCode: '+62', flag: '🇮🇩' },
  { code: 'BR' as CountryCode, name: 'Brazil', dialCode: '+55', flag: '🇧🇷' },
  { code: 'MX' as CountryCode, name: 'Mexico', dialCode: '+52', flag: '🇲🇽' },
  { code: 'AR' as CountryCode, name: 'Argentina', dialCode: '+54', flag: '🇦🇷' },
  { code: 'CL' as CountryCode, name: 'Chile', dialCode: '+56', flag: '🇨🇱' },
  { code: 'CO' as CountryCode, name: 'Colombia', dialCode: '+57', flag: '🇨🇴' },
  { code: 'PE' as CountryCode, name: 'Peru', dialCode: '+51', flag: '🇵🇪' },
  { code: 'ZA' as CountryCode, name: 'South Africa', dialCode: '+27', flag: '🇿🇦' },
  { code: 'EG' as CountryCode, name: 'Egypt', dialCode: '+20', flag: '🇪🇬' },
  { code: 'TR' as CountryCode, name: 'Turkey', dialCode: '+90', flag: '🇹🇷' },
  { code: 'RU' as CountryCode, name: 'Russia', dialCode: '+7', flag: '🇷🇺' },
  { code: 'PL' as CountryCode, name: 'Poland', dialCode: '+48', flag: '🇵🇱' },
  { code: 'CZ' as CountryCode, name: 'Czech Republic', dialCode: '+420', flag: '🇨🇿' },
  { code: 'HU' as CountryCode, name: 'Hungary', dialCode: '+36', flag: '🇭🇺' },
  { code: 'RO' as CountryCode, name: 'Romania', dialCode: '+40', flag: '🇷🇴' },
  { code: 'SK' as CountryCode, name: 'Slovakia', dialCode: '+421', flag: '🇸🇰' },
  { code: 'SI' as CountryCode, name: 'Slovenia', dialCode: '+386', flag: '🇸🇮' },
  { code: 'HR' as CountryCode, name: 'Croatia', dialCode: '+385', flag: '🇭🇷' },
  { code: 'BA' as CountryCode, name: 'Bosnia and Herzegovina', dialCode: '+387', flag: '🇧🇦' },
  { code: 'ME' as CountryCode, name: 'Montenegro', dialCode: '+382', flag: '🇲🇪' },
  { code: 'MK' as CountryCode, name: 'North Macedonia', dialCode: '+389', flag: '🇲🇰' },
  { code: 'AL' as CountryCode, name: 'Albania', dialCode: '+355', flag: '🇦🇱' },
  { code: 'RS' as CountryCode, name: 'Serbia', dialCode: '+381', flag: '🇷🇸' },
];

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ value = '', onChange, defaultCountry = 'AU', placeholder, className, disabled, ...props }, ref) => {
    const [selectedCountry, setSelectedCountry] = useState(countries.find(c => c.code === defaultCountry) || countries[0]);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [open, setOpen] = useState(false);
    const [isValid, setIsValid] = useState(true);
    // DED-1455: Track if we're the source of the value change to prevent feedback loops
    const isInternalChange = React.useRef(false);
    const lastExternalValue = React.useRef(value);

    // Parse initial value if provided - DED-1455: Only run when value changes externally
    useEffect(() => {
      // Skip if this change was triggered by our own onChange
      if (isInternalChange.current) {
        isInternalChange.current = false;
        lastExternalValue.current = value;
        return;
      }
      
      // Skip if value hasn't actually changed
      if (value === lastExternalValue.current && phoneNumber !== '') {
        return;
      }
      
      lastExternalValue.current = value;
      
      if (value) {
        try {
          const parsed = parsePhoneNumber(value);
          if (parsed && parsed.isValid()) {
            const country = countries.find(c => c.code === parsed.country);
            if (country) {
              setSelectedCountry(country);
              setPhoneNumber(parsed.nationalNumber);
              setIsValid(true);
            } else {
              // If country not found, keep as is
              setPhoneNumber(value);
              setIsValid(false);
            }
          } else {
            // If parsing fails or number is invalid, treat as raw input
            // Check if it starts with a country code and strip ALL leading country codes
            let processedValue = value;
            let foundCountry = false;
            for (const country of countries) {
              if (processedValue.startsWith(country.dialCode)) {
                processedValue = processedValue.substring(country.dialCode.length);
                // Remove any additional country codes that might have been added accidentally
                while (processedValue.startsWith(country.dialCode)) {
                  processedValue = processedValue.substring(country.dialCode.length);
                }
                setSelectedCountry(country);
                setPhoneNumber(processedValue);
                setIsValid(true);
                foundCountry = true;
                break;
              }
            }
            if (!foundCountry) {
              // No country code found, assume it's just the national number for current country
              setPhoneNumber(value);
              setIsValid(true);
            }
          }
        } catch (error) {
          // If parsing fails, treat as raw national number
          setPhoneNumber(value);
          setIsValid(false);
        }
      } else {
        setPhoneNumber('');
        setIsValid(true);
      }
    }, [value]);

    const handleCountryChange = (country: typeof countries[0]) => {
      setSelectedCountry(country);
      setOpen(false);
      const cleanInput = phoneNumber.replace(/\s+/g, '');
      const fullNumber = country.dialCode + cleanInput;

      // DED-1455: Mark as internal change before calling onChange
      isInternalChange.current = true;

      try {
        const parsed = parsePhoneNumber(fullNumber, country.code);
        setIsValid(!parsed || parsed.isValid());

        if (parsed && parsed.isValid()) {
          onChange?.(parsed.number);
        } else {
          onChange?.(fullNumber);
        }
      } catch (error) {
        setIsValid(false);
        onChange?.(fullNumber);
      }
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;

      // Set the display value directly
      setPhoneNumber(input);

      // Validate and update the external value
      let cleanInput = input.replace(/\s+/g, '');

      // Make sure the input doesn't already start with a country code
      if (cleanInput.startsWith(selectedCountry.dialCode)) {
        cleanInput = cleanInput.substring(selectedCountry.dialCode.length);
      }

      const fullNumber = selectedCountry.dialCode + cleanInput;

      // DED-1455: Mark as internal change before calling onChange
      isInternalChange.current = true;

      // For partial inputs, don't try to parse - just validate basic format
      if (cleanInput.length < 3) {
        // Too short to validate properly
        setIsValid(true);
        onChange?.(fullNumber);
      } else {
        try {
          const parsed = parsePhoneNumber(fullNumber, selectedCountry.code);
          const valid = parsed ? parsed.isValid() : false;
          setIsValid(valid);

          if (parsed && parsed.isValid()) {
            onChange?.(parsed.number);
          } else {
            onChange?.(fullNumber);
          }
        } catch (error) {
          setIsValid(false);
          onChange?.(fullNumber);
        }
      }
    };

    return (
      <div className={cn('flex', className)}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className={cn(
                'flex h-10 w-[140px] items-center justify-between rounded-r-none border-r-0 px-3 text-sm',
                !isValid && 'border-red-500'
              )}
              disabled={disabled}
            >
              <span className="flex items-center gap-2">
                <span className="text-lg">{selectedCountry.flag}</span>
                <span>{selectedCountry.dialCode}</span>
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0">
            <Command>
              <CommandInput placeholder="Search countries..." />
              <CommandList>
                <CommandEmpty>No country found.</CommandEmpty>
                <CommandGroup>
                  {countries.map((country) => (
                    <CommandItem
                      key={country.code}
                      value={`${country.name} ${country.dialCode}`}
                      onSelect={() => handleCountryChange(country)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          selectedCountry.code === country.code ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span className="mr-2 text-lg">{country.flag}</span>
                      <span className="flex-1">{country.name}</span>
                      <span className="text-muted-foreground">{country.dialCode}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Input
          {...props}
          ref={ref}
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder={placeholder || '400 000 000'}
          className={cn(
            'rounded-l-none h-10',
            !isValid && 'border-red-500 focus-visible:ring-red-500'
          )}
          disabled={disabled}
        />
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';

export { PhoneInput };
